import React, { MouseEventHandler, useMemo } from 'react'

import { Color, getContrastTextColor } from 'themes'
import { Severity } from 'utils/types'

import { useCustomization } from 'redux/hooks/Customization'
import { useTheme } from '@mui/material'

import Avatar from 'components_new/atoms/Avatar'

interface GroupAvatarProps {
  className?: string
  color?: Color | Severity
  onClick?: (() => void) | MouseEventHandler<HTMLElement>
  size?: 'xsmall' | 'small' | 'medium' | 'large'
  title?: string
  sx?: object
}

const SEVERITIES: Severity[] = ['info', 'error', 'success', 'warning']

const isSeverity = (color: Color | Severity): color is Severity =>
  (SEVERITIES as string[]).includes(color)

const GroupAvatar = React.forwardRef((props: GroupAvatarProps, ref: any) => {
  const { className, color, onClick, size, sx, title } = props

  const theme = useTheme()
  const customization = useCustomization()

  const customColor =
    color && !isSeverity(color) && customization.profile_colors
      ? customization.profile_colors[color]
      : undefined

  /*-- background color --*/
  const backgroundColor = useMemo(() => {
    // custom
    if (customColor) {
      return customColor
    }

    // default
    return color ? `${color}.background` : 'grey.100'
  }, [customization.profile_colors, color])

  /*-- text color --*/
  const textColor = useMemo(() => {
    // custom
    if (customColor) {
      return getContrastTextColor(customColor)
    }

    // default
    return color
      ? getContrastTextColor(theme.palette[color].background)
      : 'text.secondary'
  }, [customization.profile_colors, color])

  return (
    <Avatar
      ref={ref}
      alt={title?.toUpperCase()}
      bgcolor={backgroundColor}
      className={className}
      onClick={onClick}
      size={size === 'xsmall' ? 'small' : size}
      sx={{
        color: `${textColor}!important`,
        transition: 'all 0.2s ease',
        ...(size === 'xsmall' ? { fontSize: '0.7rem', height: '16px' } : null),
        ...(onClick
          ? {
              cursor: 'pointer',
              '&:hover': {
                transform: 'scale(1.05)'
              }
            }
          : null),
        ...sx
      }}
      variant="rounded"
    />
  )
})

export default GroupAvatar
